import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { App, Button, Grid } from 'antd';
import RetryDataState from '../components/common/RetryDataState';
import { DATA_STATE, Skeleton, resourceStateOf } from '../components/loading';

import ContextBar from '../components/common/ContextBar';
import Guidance from '../components/common/Guidance';
import { confirmDestructive } from '../components/common/confirmDestructive';
import { preloadCharts } from '../components/charts/LazyCharts';
import AddChartModal from '../components/dashboards/AddChartModal';
import AddHeadingModal from '../components/dashboards/AddHeadingModal';
import DashboardControls, { OWN_DATES } from '../components/dashboards/DashboardControls';
import DashboardGrid from '../components/dashboards/DashboardGrid';
import VersionConflictModal from '../components/dashboards/VersionConflictModal';
import SourceMark from '../components/notifications/SourceMark';
import NameDashboardModal from '../components/reports/NameDashboardModal';
import SaveControlsModal from '../components/dashboards/SaveControlsModal';
import { absoluteUrl, dashboardActions, deleteDashboardCopy } from '../components/reports/dashboardActions';
import { useCollectionContext } from '../contexts/CollectionContext';
import { useOptionalCardDefinitions } from '../contexts/CardDefinitionsContext';
import useDashboard from '../hooks/useDashboard';
import useDashboardFilterFields from '../hooks/useDashboardFilterFields';
import useDashboardRun, { NOT_DRAWN } from '../hooks/useDashboardRun';
import { copyableTiles } from '../hooks/useDashboards';
import { useDirtyForm } from '../hooks/useDirtyForm';
import {
	DASHBOARD_SCOPE, MAX_TILES, createDashboard, dashboardRefusal, deleteDashboard, demoteDashboard, promoteDashboard,
	refusedTileIndex, updateDashboard, versionConflictOf,
} from '../services/dashboards';
import { collectionCrumbs } from '../utils/breadcrumbs';
import { rangeLabel } from '../utils/chartRanges';
import { copyTextToClipboard } from '../utils/clipboard';
import {
	FILTER_TOO_LONG, FILTER_UNREADABLE, PAUSED, addChartTile, addTextTile, changedControls, controlsFromSearch,
	controlsToSearch, dashboardPath, draftControlsTouched, draftIsClean, draftOf, draftTilesChanged, effectiveControls,
	moveTile, patchTile, removeTile, reorderTiles, storedControls, withoutControls,
} from '../utils/dashboards';
import { describeQuery } from '../utils/filterSummary';
import { formatRelativeToNow } from '../utils/formatters';
import { normalizeId } from '../utils/ids';
import { canManage, canReadCharts } from '../utils/permissions';
import { chartElements } from '../components/reports/useChartShare';
import { editChartPath, newChartPath } from './ReportsPage';
import useChartPlacements from '../hooks/useChartPlacements';
import useElementChoices from '../hooks/useElementChoices';
import QueryRefusalPick from '../components/items/QueryRefusalPick';
import { elementRefusalOf, tellCardsApart, withElementChoicesField, writeElementPick } from '../utils/elementRefusal';
import { ancestorsOf, subCollectionsOf } from '../utils/collectionFamily';
import { ELEMENT_ID_RE } from '../utils/kql';
import { anchorOf, print as platformPrint } from '../platform';

/** The one sentence for a dashboard that is gone, not yours or unreadable (ruled d26). */
export const DASHBOARD_GONE = 'This dashboard is no longer available.';
/** What the bar says while someone arranges (ruled d17). */
export const ARRANGING = 'Arranging. Others see the last saved layout until you press Done.';
/** What the bar says once the page stopped re-running on an idle tab, and its one message when the server cannot read the filter (a hand-edited `?q=`) — shared with the Overview (utils/dashboards). */
export { FILTER_UNREADABLE, PAUSED };
const FULL = `A dashboard holds at most ${MAX_TILES} tiles. Remove one before adding another.`;
const NO_TILES = Object.freeze([]);

/**
 * The dashboard page — `/collections/:collectionId/dashboards/:dashboardId`
 * (charts A6, plan §6.5; standards §5 "A dashboard arranges, and saves once").
 *
 * Its own context bar: the trail to Reports, the name as a plain title, a
 * meta line — the tier ("Pinned for everyone", "Yours", "From {parent}"),
 * "Counts the items you can see", when it last ran with Refresh (or "Paused.
 * Refresh to update.") — **Arrange** as a labelled secondary for whoever may,
 * and the ⋯ in the four groups (dashboardActions). The bar's second row holds
 * the controls: the date range, then the filter; on a phone a Filters button.
 * The controls live in the URL (`?range=`, `?q=`) over the stored ones, so a
 * filtered dashboard is a link.
 *
 * Under it, the grid: ONE batch run for every available chart tile
 * (useDashboardRun — live, paused when idle, one request at a time), each
 * tile failing on its own.
 *
 * ARRANGE (ruled d17) is a draft of the tiles and the default controls in
 * this browser: drags, sizes, adds and removes change the draft only, the
 * page registers with UnsavedChangesContext while it differs, and **Done**
 * saves the whole layout in ONE PATCH with the version it read. A 409
 * `version_conflict` asks: Load their version, or Save mine over it. A tile
 * the server refuses is said on that tile. **Cancel** drops the draft.
 */
export default function DashboardPage() {
	// One mounted page per dashboard (the second review's F2): Back, Forward
	// or Duplicate's navigate to another dashboard starts from nothing — no
	// draft, refusal, window or answer of the last one survives.
	const { dashboardId } = useParams();
	return <DashboardView key={dashboardId || 'none'} />;
}

function DashboardView() {
	const { dashboardId: rawDashboardId } = useParams();
	const { collection, resolvedCollectionId, collectionError, refreshCollection } = useCollectionContext() || {};
	const collectionId = resolvedCollectionId;
	const navigate = useNavigate();
	const location = useLocation();
	const { modal, message } = App.useApp();
	const [searchParams, setSearchParams] = useSearchParams();
	const screens = Grid.useBreakpoint();
	const phone = screens.md === false;

	const allowed = Boolean(collection) && canReadCharts(collection);
	const isManager = Boolean(collection) && canManage(collection);
	const placements = useChartPlacements(collectionId);
	const reportsPath = `/collections/${collectionId}/reports`;

	// The chart drawer's code starts downloading with the page, not after the numbers.
	useEffect(() => { preloadCharts().catch(() => {}); }, []);

	const { dashboard, status, dashboardId, replace, reload } = useDashboard(collectionId, rawDashboardId, { enabled: allowed });
	const mayArrange = dashboard?.canEdit === true;

	// The controls: the URL's over the stored ones (read before Arrange, which
	// seeds its draft from the controls in force).
	const stored = useMemo(() => storedControls(dashboard), [dashboard]);
	const fromUrl = useMemo(() => controlsFromSearch(searchParams), [searchParams]);

	// ------------------------------------------------------------ arrange
	// The draft: { tiles, controls, seedControls, version } while arranging, else null.
	const [draft, setDraft] = useState(null);
	const arranging = Boolean(draft);
	// What a refused save said of a tile, by tile id.
	const [refusals, setRefusals] = useState(() => new Map());
	const [saving, setSaving] = useState(false);
	// A 409's { version, dashboard } while its window is open.
	const [conflict, setConflict] = useState(null);
	// The save a 409 offers to send again: { patch, after } (Save mine over it).
	const [pending, setPending] = useState(null);
	// Done asking whether changed controls are everyone's defaults.
	const [askControls, setAskControls] = useState(false);
	// A save answered 404: the dashboard went away, or became someone else's.
	const [lost, setLost] = useState(false);
	const [adding, setAdding] = useState(null); // 'chart' | 'heading' | null
	const dirty = arranging && !draftIsClean(draft, dashboard);
	const { guardDiscard } = useDirtyForm(dirty ? 'arranged' : 'saved', { enabled: arranging });

	// The draft starts from the controls IN FORCE (the link's view, F3) and
	// remembers the version it was arranged from (F1).
	const startArranging = useCallback(() => {
		if (!dashboard) return;
		setDraft(draftOf(dashboard, effectiveControls(stored, fromUrl)));
		setRefusals(new Map());
	}, [dashboard, stored, fromUrl]);
	const stopArranging = useCallback(() => {
		setDraft(null);
		setRefusals(new Map());
		setConflict(null);
		setPending(null);
		setAskControls(false);
		setAdding(null);
	}, []);

	// A new dashboard opens in Arrange (NewDashboardPage hands it `arrange`).
	const arrangeAsked = useRef(Boolean(location.state && location.state.arrange));
	useEffect(() => {
		if (!arrangeAsked.current || status !== 'ready' || !dashboard) return;
		arrangeAsked.current = false;
		if (mayArrange) startArranging();
		navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
	}, [status, dashboard, mayArrange, startArranging, navigate, location.pathname, location.search]);

	const changeDraft = useCallback((change) => {
		setDraft((current) => (current ? { ...current, ...change(current) } : current));
	}, []);
	const tileAction = useCallback((tile, action, value) => {
		changeDraft((current) => {
			if (action === 'size') return { tiles: patchTile(current.tiles, tile.id, { w: value }) };
			if (action === 'height') return { tiles: patchTile(current.tiles, tile.id, { h: value }) };
			if (action === 'move') return { tiles: moveTile(current.tiles, tile.id, value) };
			if (action === 'remove') return { tiles: removeTile(current.tiles, tile.id) };
			return {};
		});
		if (action === 'remove') {
			setRefusals((current) => {
				if (!current.has(tile.id)) return current;
				const next = new Map(current);
				next.delete(tile.id);
				return next;
			});
		}
	}, [changeDraft]);
	const reorder = useCallback((ids) => changeDraft((current) => ({ tiles: reorderTiles(current.tiles, ids) })), [changeDraft]);

	// ------------------------------------------------------------ controls
	// Arranging edits a draft of them; otherwise the URL wins.
	const controls = arranging ? draft.controls : effectiveControls(stored, fromUrl);
	const changeControls = (patch) => {
		if (arranging) {
			changeDraft((current) => ({ controls: { ...current.controls, ...patch } }));
			return;
		}
		setSearchParams(controlsToSearch(searchParams, patch, stored), { replace: true });
	};
	const forEveryone = dashboard?.scope === DASHBOARD_SCOPE.COLLECTION;
	const filterFields = useDashboardFilterFields(collection, collectionId, {
		// A default saved on a dashboard for everyone may name no private tag.
		mode: arranging && forEveryone ? 'collection' : 'live',
		enabled: allowed,
	});

	// ------------------------------------------------------------ the run
	const savedTiles = dashboard?.tiles;
	const draftTiles = draft?.tiles;
	const tiles = useMemo(() => {
		if (draftTiles) return draftTiles;
		return Array.isArray(savedTiles) ? savedTiles : NO_TILES;
	}, [draftTiles, savedTiles]);
	const { results, loading, ranAt, paused, filterError, refresh } = useDashboardRun({
		tiles,
		collectionId,
		range: controls.range === null ? undefined : controls.range,
		filter: controls.filter,
		enabled: allowed && status === 'ready',
	});
	const anyChart = tiles.some((tile) => tile && tile.kind === 'chart' && tile.available);

	// "Updated 2 minutes ago" keeps time while the page stays open.
	const [, setTick] = useState(0);
	useEffect(() => {
		const timer = setInterval(() => setTick((n) => n + 1), 30000);
		return () => clearInterval(timer);
	}, []);

	// Each chart's card, for its notes' words and a choice's own colours.
	const cardDefinitions = useOptionalCardDefinitions();
	const cardsById = useMemo(() => cardDefinitions?.cardsById || {}, [cardDefinitions?.cardsById]);
	const getElementsForCard = cardDefinitions?.getElementsForCard;
	const elementSetsById = cardDefinitions?.elementSetsById;
	const noteMissingCards = cardDefinitions?.noteMissingCards;
	const cardIds = useMemo(() => [...new Set(tiles
		.map((tile) => (tile && tile.available && tile.chart ? normalizeId(tile.chart.spec?.card_id) : null))
		.filter(Boolean))], [tiles]);
	useEffect(() => {
		if (typeof noteMissingCards !== 'function') return;
		const missing = cardIds.filter((id) => !cardsById[id]);
		if (missing.length) noteMissingCards(missing);
	}, [cardIds, cardsById, noteMissingCards]);
	const contexts = useMemo(() => {
		const map = new Map();
		tiles.forEach((tile) => {
			const chart = tile && tile.available ? tile.chart : null;
			const cardId = chart ? normalizeId(chart.spec?.card_id) : null;
			const card = cardId ? cardsById[cardId] : null;
			if (!chart || !card || map.has(normalizeId(chart._id))) return;
			map.set(normalizeId(chart._id), {
				cardName: card.name,
				elements: chartElements(chart, getElementsForCard, { cardsById, elementSetsById }),
			});
		});
		return map;
	}, [tiles, cardsById, elementSetsById, getElementsForCard]);
	const chartContext = useCallback((chart) => contexts.get(normalizeId(chart && chart._id)) || null, [contexts]);

	// ------------------------------------------------------------ Done
	// One save of what the draft changed, at the version it was arranged from
	// (F1: never the version the page adopted since). `after` puts the URL as
	// it should read once saved.
	// An element the filter being saved could mean several of, where none of
	// the dashboard's charts' cards has it (400 `invalid_filter`, `cause`
	// `ambiguous_element`; a name the charts' cards define is written as the
	// element group across them, never asked — ⚑ D1): the person picks one,
	// or any of these cards, and the save goes again with the pick.
	// A pick answers exactly the filter it was made for: a new filter, or
	// leaving Arrange (Cancel, Done), forgets it.
	const savePicks = useElementChoices(draft ? String((draft.controls && draft.controls.filter) || '') : null);
	const submitSave = async (patch, after, elementChoices) => {
		if (!draft || !dashboard) return;
		setSaving(true);
		try {
			// The picks ride only when there are some: a save with none carries
			// no `elementChoices` key at all.
			const saved = await updateDashboard(collectionId, dashboardId, withElementChoicesField(patch, elementChoices));
			savePicks.reset();
			replace(saved);
			stopArranging();
			if (typeof after === 'function') after();
			message.success('Dashboard saved.');
		} catch (e) {
			if (e?.response?.status === 404) {
				// Gone, or someone else's now: the page's one sentence (L5).
				stopArranging();
				setLost(true);
				return;
			}
			const stale = versionConflictOf(e);
			if (stale) {
				setPending({ patch, after });
				setConflict(stale);
				return;
			}
			if (savePicks.take(e)) return;
			const at = refusedTileIndex(e);
			const tile = at !== null && Array.isArray(patch.tiles) ? patch.tiles[at] : null;
			if (tile) {
				setRefusals(new Map([[tile.id, dashboardRefusal(e, 'This tile could not be saved.')]]));
				message.error('One tile could not be saved. It says why.');
				return;
			}
			message.error(dashboardRefusal(e, 'Could not save the dashboard.'));
		} finally {
			setSaving(false);
		}
	};
	const sendSave = (patch, after) => savePicks.attempt(choices => submitSave(patch, after, choices));
	const layoutPatch = () => ({ tiles: draft.tiles, version: draft.version });
	// Only what the person CHANGED while arranging is theirs to save (F3): the
	// controls as they came in from the link are their view, not a change.
	// `touched` — the keys they changed; `toSave` — those that differ from the
	// stored defaults (a key put back to the default needs no save).
	const controlChanges = () => {
		const touched = changedControls(draft.controls, draft.seedControls);
		const toSave = {};
		Object.keys(changedControls(draft.controls, stored)).forEach((key) => { if (key in touched) toSave[key] = touched[key]; });
		return { touched, toSave };
	};
	// The link once the touched keys are the view: written over, a default cleared.
	const toView = (touched) => () => setSearchParams(controlsToSearch(searchParams, touched, stored), { replace: true });
	const done = () => {
		if (!draft) return;
		const tilesChanged = draftTilesChanged(draft, dashboard);
		const { touched, toSave } = controlChanges();
		if (Object.keys(toSave).length) {
			setAskControls(true);
			return;
		}
		// Never touched, or put back to the defaults: the layout alone.
		const after = draftControlsTouched(draft) ? toView(touched) : undefined;
		if (!tilesChanged) {
			stopArranging();
			if (after) after();
			return;
		}
		sendSave(layoutPatch(), after);
	};
	const saveDefaults = () => {
		setAskControls(false);
		const { touched, toSave } = controlChanges();
		// The whole layout with them, as every save of the layout sends it.
		const patch = { ...layoutPatch(), controls: toSave };
		// Only the keys changed here leave the link: the rest of its view stays.
		sendSave(patch, () => setSearchParams(withoutControls(searchParams, { range: 'range' in touched, filter: 'filter' in touched }), { replace: true }));
	};
	const keepForView = () => {
		setAskControls(false);
		const { touched } = controlChanges();
		if (!draftTilesChanged(draft, dashboard)) {
			stopArranging();
			toView(touched)();
			return;
		}
		sendSave(layoutPatch(), toView(touched));
	};
	const cancel = () => guardDiscard(stopArranging);
	const loadTheirs = () => {
		if (conflict?.dashboard) replace(conflict.dashboard);
		else reload();
		stopArranging();
	};
	// The same save again — the same keys, controls only those changed (L12) —
	// at the version the 409 named.
	const saveMine = () => {
		const version = conflict?.version;
		const again = pending;
		setConflict(null);
		if (again) sendSave({ ...again.patch, version }, again.after);
	};

	// ------------------------------------------------------------ the ⋯
	const [naming, setNaming] = useState(null); // 'rename' | 'duplicate'
	const promote = async () => {
		try {
			replace(await promoteDashboard(collectionId, dashboardId));
		} catch (e) {
			message.error(dashboardRefusal(e, 'Could not pin the dashboard for everyone.'));
		}
	};
	const demote = async () => {
		try {
			replace(await demoteDashboard(collectionId, dashboardId));
		} catch (e) {
			message.error(dashboardRefusal(e, 'Could not unpin the dashboard.'));
		}
	};
	const copyLink = async () => {
		const ok = await copyTextToClipboard(absoluteUrl(`${location.pathname}${location.search}`));
		if (ok) message.success('Link copied to clipboard');
		else message.error('Could not copy the link.');
	};
	const confirmDelete = () => {
		confirmDestructive(modal, {
			...deleteDashboardCopy(dashboard),
			okText: 'Delete',
			onOk: async () => {
				try {
					await deleteDashboard(collectionId, dashboardId);
					message.success(`Deleted "${dashboard?.name || 'the dashboard'}". History can restore it.`);
					navigate(reportsPath);
				} catch (e) {
					message.error(dashboardRefusal(e, 'Could not delete the dashboard.'));
				}
			},
		});
	};
	const submitName = async (values) => {
		if (naming === 'duplicate') {
			const created = await createDashboard(collectionId, {
				...values,
				tiles: copyableTiles(dashboard?.tiles),
				controls: dashboard?.controls || undefined,
			});
			message.success(`Made "${created?.name || values.name}", a copy only you see.`);
			if (created?._id) navigate(dashboardPath(collectionId, normalizeId(created._id)));
			return;
		}
		const saved = await updateDashboard(collectionId, dashboardId, values);
		// While arranging, a rename takes only the name and description: the
		// layout and version under the draft stay what Arrange began from (F1).
		if (arranging && saved && dashboard) {
			replace({ ...dashboard, name: saved.name, description: saved.description, updatedAt: saved.updatedAt, updatedBy: saved.updatedBy });
		} else replace(saved);
	};

	const goReports = () => navigate(reportsPath);
	// While arranging, nothing else writes the dashboard's layout or tier: a
	// pin, an unpin or a delete would move its version under the draft, and
	// Done would meet it as a 409. A rename does not touch the layout or the
	// version (ruled), so it stays.
	// Pin to Home and the Overview choices write no dashboard (charts A7): they
	// stay while arranging.
	const menuActions = dashboard ? dashboardActions({
		dashboard,
		isManager,
		...placements.dashboard(dashboard),
		onReports: goReports,
		onRename: () => setNaming('rename'),
		onDuplicate: arranging ? undefined : () => setNaming('duplicate'),
		onPromote: arranging ? undefined : promote,
		onDemote: arranging ? undefined : demote,
		onCopyLink: copyLink,
		onPrint: () => { platformPrint(undefined, { anchor: anchorOf() }); },
		onDelete: arranging ? undefined : confirmDelete,
	}) : [{ key: 'reports', group: 'navigate', glyph: 'open', label: 'Go to Reports', onClick: goReports }];

	// ------------------------------------------------------------ the bar
	const collectionGone = !collection && resourceStateOf(collectionError) === 'not-found';
	const collectionFailed = !collection && collectionError && !collectionGone;
	const gone = collectionGone || status === 'gone' || lost;
	let title = dashboard?.name;
	if (!title) title = gone || collectionFailed || status === 'error' || (collection && !allowed) ? 'Dashboard' : 'Loading dashboard…';

	const tierMark = !dashboard ? null : forEveryone ? (
		<SourceMark word='Pinned for everyone' phrase='Pinned for everyone by a collection manager' className='chart-page__tier' />
	) : (
		<SourceMark word='Yours' phrase='Only you see this dashboard' own className='chart-page__tier' />
	);

	let updated = null;
	if (loading) updated = 'Running…';
	else if (paused) updated = PAUSED;
	else if (ranAt) updated = `Updated ${formatRelativeToNow(ranAt)}`;

	const below = dashboard ? (
		<span className='chart-page__meta dashboard-page__meta'>
			{tierMark}
			{arranging ? (
				<span className='chart-page__fact dashboard-page__arranging' role='status'>{ARRANGING}</span>
			) : (
				<>
					<span className='chart-page__fact'>Counts the items you can see</span>
					{anyChart && updated ? <span className='chart-page__fact'>{updated}</span> : null}
					{anyChart ? <Button type='link' size='small' className='chart-page__refresh' onClick={refresh} disabled={loading}>Refresh</Button> : null}
				</>
			)}
		</span>
	) : null;

	let actions = null;
	if (dashboard && mayArrange) {
		actions = arranging ? (
			<>
				<Button onClick={cancel} disabled={saving}>Cancel</Button>
				<Button type='primary' onClick={done} loading={saving}>Done</Button>
			</>
		) : <Button onClick={startArranging}>Arrange</Button>;
	}

	const showControls = Boolean(dashboard) && status === 'ready' && allowed;
	const bar = (
		<ContextBar
			breadcrumb={collectionCrumbs(collection, collectionId, { label: 'Reports', to: reportsPath })}
			title={title}
			below={below}
			actions={actions}
			menuActions={menuActions}
			className='dashboard-page__bar'
		>
			{showControls ? (
				<DashboardControls
					controls={controls}
					onChange={changeControls}
					fields={filterFields}
					collectionId={collectionId}
					scopeKey={collectionId ? `dashboard:${collectionId}` : null}
					phone={phone}
					hint={arranging ? `A date range or filter you change here can be saved ${forEveryone ? 'for everyone' : "as this dashboard's default"} when you press Done.` : null}
				/>
			) : null}
		</ContextBar>
	);

	// ------------------------------------------------------------ the body
	const full = tiles.length >= MAX_TILES;
	// One failure the dashboard's filter caused on EVERY chart is the page's
	// to say, once (F4): the tiles say they were not drawn, never the same
	// sentence each, never Edit chart.
	const lifted = useMemo(() => {
		const runnable = tiles.filter((tile) => tile && tile.kind === 'chart' && tile.available);
		if (!runnable.length) return null;
		let first = null;
		for (const tile of runnable) {
			const slot = results.get(tile.id);
			const error = slot && slot.ok === false ? slot.error : null;
			if (!error || !error.control || error.code === 'not_drawn') return null;
			const key = `${error.code}|${error.message}`;
			if (!first) first = { key, message: error.message };
			else if (first.key !== key) return null;
		}
		return first;
	}, [tiles, results]);
	const shownResults = useMemo(() => {
		if (!lifted) return results;
		const out = new Map(results);
		tiles.forEach((tile) => {
			if (tile && tile.kind === 'chart' && tile.available) out.set(tile.id, { ok: false, chart_id: tile.chart_id, error: { code: 'not_drawn', message: NOT_DRAWN, control: 'filter' } });
		});
		return out;
	}, [lifted, results, tiles]);
	// An element the dashboard's filter could mean several of on the charts'
	// cards (a run's slot `invalid_filter`, `cause` `ambiguous_element`,
	// element ids step 3): one question for the page — every refused chart's
	// candidates for the same name, together — answered by writing the pick
	// into the filter (writeElementPick): an element's id, or the group across
	// them, which each chart reads by its own card.
	const runRefusal = useMemo(() => {
		const refusals = tiles
			.filter(tile => tile && tile.kind === 'chart' && tile.available)
			.map((tile) => {
				const slot = results.get(tile.id);
				const error = slot && slot.ok === false && slot.error && slot.error.control ? slot.error : null;
				return error ? elementRefusalOf(error) : null;
			})
			.filter(refusal => refusal && refusal.code === 'ambiguous_element');
		if (!refusals.length || refusals.some(refusal => refusal.choiceKey !== refusals[0].choiceKey)) return null;
		const byId = new Map();
		refusals.flatMap(refusal => refusal.candidates).forEach((candidate) => {
			const known = byId.get(candidate.elementId);
			if (!known) { byId.set(candidate.elementId, { ...candidate, cards: [...candidate.cards] }); return; }
			candidate.cards.forEach((card) => { if (!known.cards.some(entry => entry.cardId === card.cardId)) known.cards.push(card); });
		});
		// Each chart's refusal was told apart on its own (KPR-275): merged, two
		// of its cards can read alike again ("Caliber on Suppressor" twice, the
		// review's R275-D1). Told apart here with what the page knows: global,
		// the name of this collection or of one in its family the page lists
		// (its ancestors, the sub-collections the person reads), else the key.
		const familyNames = new Map();
		[collection, ...ancestorsOf(collection), ...subCollectionsOf(collection)].forEach((doc) => {
			const id = normalizeId(doc?._id);
			const name = typeof doc?.name === 'string' ? doc.name.trim() : '';
			if (id && name && !familyNames.has(id)) familyNames.set(id, name);
		});
		const keyOf = cardId => String(cardsById[normalizeId(cardId)]?.key || '').trim();
		const ownOf = (cardId) => {
			const card = cardsById[normalizeId(cardId)];
			if (!card) return '';
			if (card.scope === 'global') return 'global';
			return familyNames.get(normalizeId(card.collection_id)) || keyOf(cardId);
		};
		return { ...refusals[0], candidates: tellCardsApart([...byId.values()], { ownOf, keyOf }) };
	}, [tiles, results, collection, cardsById]);
	// A listed card this page's cache does not hold yet cannot be told apart
	// by it: ask the cache for it, as for a chart's card above.
	useEffect(() => {
		if (!runRefusal || typeof noteMissingCards !== 'function') return;
		const missing = [...new Set(runRefusal.candidates.flatMap(candidate => candidate.cards.map(card => normalizeId(card.cardId))))].filter(id => id && !cardsById[id]);
		if (missing.length) noteMissingCards(missing);
	}, [runRefusal, cardsById, noteMissingCards]);
	// The charts that read the name fine keep reading their own element (the
	// P4 review's R-DB1): a written id their card does not hold would leave
	// them "Not filtered". So the answer also names, for each chart that was
	// NOT asked, the element its own card answers to under that name — the
	// server reads a run's name on the chart's card first — and the text is
	// the group of them all: each chart then reads its own card's element
	// (keepr-api plan.js applies a group where the card holds one of its ids).
	// Only a name at a root: a hop's leaf or a reference is answered as asked.
	const answeredElsewhere = useMemo(() => {
		if (!runRefusal || runRefusal.reference || String(runRefusal.choiceKey).includes('.') || typeof getElementsForCard !== 'function') return [];
		const name = runRefusal.choiceKey;
		const out = [];
		tiles.forEach((tile) => {
			if (!tile || tile.kind !== 'chart' || !tile.available) return;
			const slot = results.get(tile.id);
			if (slot && slot.ok === false && elementRefusalOf(slot.error)) return;
			const cardId = normalizeId(tile.chart?.spec?.card_id);
			if (!cardId) return;
			const own = (getElementsForCard(cardId) || []).find(el => el && el.name === name && ELEMENT_ID_RE.test(String(el.id || '')));
			if (own && !out.includes(own.id)) out.push(own.id);
		});
		return out;
	}, [runRefusal, tiles, results, getElementsForCard]);
	const answerRun = (value) => {
		const picked = [].concat(value);
		const extra = answeredElsewhere.filter(id => !picked.includes(id));
		const ids = extra.length ? [...picked, ...extra] : value;
		changeControls({ filter: writeElementPick(controls.filter || '', runRefusal, ids) });
	};
	const pageFilterMessage = runRefusal ? null : (filterError || (fromUrl.tooLong && !arranging ? FILTER_TOO_LONG : null) || (lifted ? lifted.message : null));
	let emptyText = 'Nothing on this dashboard yet.';
	if (mayArrange) emptyText = phone ? 'Nothing on this dashboard yet. Arrange it on a computer or a tablet to add a chart.' : 'Nothing on this dashboard yet. Choose Arrange to add a chart.';
	let body;
	if (collection && !allowed) {
		body = <p className='dashboard-page__empty'>Charts are for people who read this collection themselves, so there is none to show here.</p>;
	} else if (gone) {
		body = (
			<p className='dashboard-page__empty'>
				{`${DASHBOARD_GONE} `}
				<Link to={reportsPath}>Go to Reports</Link>
			</p>
		);
	} else if (status === 'error' || collectionFailed) {
		body = (
			<div className='dashboard-page__error' role='alert'>
				<span>This dashboard couldn't be loaded. Trying again may help.</span>
				<Button size='small' onClick={() => (collectionFailed ? refreshCollection?.() : reload())}>Try again</Button>
			</div>
		);
	} else if (!dashboard) {
		body = (
			<RetryDataState
				state={DATA_STATE.LOADING}
				skeleton={<div className='dashboard-page__skeleton'><Skeleton variant='chart-frame' count={4} /></div>}
				onRetry={reload}
				label='this dashboard'
			/>
		);
	} else {
		const filterText = controls.filter && controls.filter.trim() ? controls.filter.trim() : '';
		body = (
			<>
				{/* Paper has no context bar (print.css), so neither the name nor the
				    controls print: a print-only head names the dashboard, whose
				    numbers they are, and the date range and filter in force. */}
				<div className='chart-page__print-head dashboard-page__print-head' aria-hidden='true'>
					<div className='chart-page__print-title'>{dashboard.name}</div>
					<div className='chart-page__print-meta'>
						{[collection?.name, 'Counts the items you can see', updated]
							.filter(Boolean).join(' · ')}
					</div>
					<div className='chart-page__print-meta'>
						{[controls.range ? `Dates: ${rangeLabel(controls.range)}` : OWN_DATES, filterText ? `Filter: ${describeQuery(filterText, filterFields) || filterText}` : null]
							.filter(Boolean).join(' · ')}
					</div>
				</div>
				{pageFilterMessage ? (
					<Guidance tone='caution' className='dashboard-page__filter-error' title={FILTER_UNREADABLE} why={[pageFilterMessage]} />
				) : null}
				<QueryRefusalPick
					className='dashboard-page__filter-error'
					refusal={runRefusal}
					onPick={answerRun}
				/>
				<QueryRefusalPick
					inline
					className='dashboard-page__filter-error'
					refusal={savePicks.refusal}
					disabled={saving}
					onPick={savePicks.retry}
					onRepick={savePicks.repick}
				/>
				{dashboard.description ? <p className='chart-page__description'>{dashboard.description}</p> : null}
				{arranging && phone ? <p className='dashboard-page__hint'>On a phone every tile is full width. Sizes apply on wider screens.</p> : null}
				<DashboardGrid
					tiles={tiles}
					results={shownResults}
					frozen={saving}
					loading={loading}
					collectionId={collectionId}
					arranging={arranging}
					phone={phone}
					refusals={refusals}
					onReorder={reorder}
					onAction={tileAction}
					onAddChart={() => setAdding('chart')}
					onAddHeading={() => setAdding('heading')}
					addDisabledReason={full ? FULL : null}
					chartContext={chartContext}
					onEditChart={(chart) => navigate(editChartPath(collectionId, normalizeId(chart._id)))}
					onRetry={refresh}
					emptyText={emptyText}
				/>
			</>
		);
	}

	return (
		<div className={`dashboard-page${arranging ? ' dashboard-page--arranging' : ''}`}>
			{bar}
			<div className='dashboard-page__body'>{body}</div>
			{dashboard ? (
				<>
					<NameDashboardModal
						open={Boolean(naming)}
						mode={naming || 'rename'}
						dashboard={dashboard}
						onClose={() => setNaming(null)}
						onSubmit={submitName}
					/>
					<AddChartModal
						open={adding === 'chart'}
						dashboard={dashboard}
						collectionId={collectionId}
						placed={tiles.filter((tile) => tile.kind === 'chart').map((tile) => tile.chart_id)}
						onAdd={(chart) => changeDraft((current) => ({ tiles: addChartTile(current.tiles, chart) }))}
						onNewChart={() => navigate(newChartPath(collectionId))}
						onClose={() => setAdding(null)}
					/>
					<AddHeadingModal
						open={adding === 'heading'}
						onAdd={(markdown) => changeDraft((current) => ({ tiles: addTextTile(current.tiles, markdown) }))}
						onClose={() => setAdding(null)}
					/>
					<SaveControlsModal
						open={askControls}
						forEveryone={forEveryone}
						saving={saving}
						onSave={saveDefaults}
						onKeepForView={keepForView}
						onClose={() => setAskControls(false)}
					/>
					<VersionConflictModal
						open={Boolean(conflict)}
						saving={saving}
						onLoadTheirs={loadTheirs}
						onSaveMine={saveMine}
						onClose={() => setConflict(null)}
					/>
				</>
			) : null}
		</div>
	);
}
